const en = {
  common: {
    appName: 'ZoningPal',
    language: 'Language',
    english: 'English',
    french: 'Français',
    dismiss: 'Dismiss',
    close: 'Close',
    home: 'Home',
    pricing: 'Pricing',
    legal: 'Legal',
    contact: 'Contact Us',
    blog: 'Blog',
    signIn: 'Sign In',
    account: 'Account',
    overview: 'Overview',
    settings: 'Account Settings',
    reports: 'My Reports',
    plansPricing: 'Plans & Pricing',
    logOut: 'Log out',
    navigation: 'Navigation',
    tagline: 'AI-Powered Zoning Intelligence',
    goHomeAria: 'Go to ZoningPal home',
    permitReview: 'Permit Review',
  },
  home: {
    heroPermitCta: 'Try Permit Review prototype (free)',
    accTitle: 'Building permit compliance prototype',
    accSubtitle:
      'ZoningPal is our production Toronto zoning intelligence platform. Permit Review is our free Canadian ACC prototype — extending the same neuro-symbolic pipeline toward NBC-aware building-permit package checking for AHJs and AEC practitioners.',
    accBullet1: '2D (PDF/CAD) + BIM/IFC permit package intake',
    accBullet2: 'Deterministic JSON rules first, then AI-assisted findings',
    accBullet3: 'Four-state results: Meets / Does not meet / Info N/A / Uncertain',
    accBullet4: 'Human-in-the-loop accept / override / escalate + audit trail',
    accBullet5: 'EN/FR UI; French NBC/QCC machine rules planned for Phase 2',
    accBullet6: 'Open API + CSV/PDF export; Canadian residency Phase 2 plan',
    accCta: 'Open free Permit Review demo',
    accNote: 'Grant demonstration prototype — no account or credits required.',
  },
  residency: {
    title: 'Canadian data residency',
    body:
      'ZoningPal is operated by a Canadian company. This grant prototype currently runs on third-party cloud services that may not yet be Canada-resident.',
    phase2:
      'Phase 2 plan: migrate to Canadian data residency (Canadian cloud regions and/or Azure OpenAI in Canada, with optional on-premise deployment for AHJs).',
  },
  permit: {
    title: 'Permit Review',
    subtitle:
      'Upload a building permit package — application details plus 2D drawings (PDF/CAD) and BIM/IFC models — for a preliminary neuro-symbolic compliance review. Machine-executable rules run first; AI assists only where information is incomplete. Free grant prototype — no account or credits required.',
    rulesetTitle: 'Deterministic ruleset',
    rulesetLoading: 'Loading machine-executable ruleset…',
    rulesetUnavailable: 'Ruleset manifest unavailable until API deploy completes.',
    rulesetExecution: 'deterministic first, then AI',
    rulesetPhase2Prefix: 'Phase 2:',
    rulesetNoteSuffix:
      'Planned adapters for NRC digitalized construction codes (XML/DITA, JSON-LD, RDF/TTL, RASE) will be configured in Phase 2 alongside a virtual integration test.',
    address: 'Project address',
    municipality: 'Municipality',
    typology: 'Building typology (NBC Part 3 / Part 9)',
    typologySelect: 'Select…',
    typologyGroupPart9: 'Part 9 — Housing & small buildings',
    typologyGroupPart3: 'Part 3 — Larger / complex buildings',
    typologyPart9House: 'House / single dwelling',
    typologyPart9Townhouse: 'Duplex, townhouse, or row house',
    typologyPart9Suite: 'Secondary suite, laneway, or garden suite',
    typologyPart9Small: 'Small building (commercial / other under Part 9 limits)',
    typologyPart3Residential: 'Multi-unit residential (apartment / condo)',
    typologyPart3Assembly: 'Assembly, business, or mercantile',
    typologyPart3Care: 'Care or treatment occupancy',
    typologyPart3Industrial: 'Industrial / high-hazard',
    typologyPart3: 'Other Part 3 or mixed use',
    typologyOther: 'Other / not sure which Part applies',
    typologyHelp:
      'Part 9 and Part 3 are divisions of the National Building Code of Canada (NBC). Part 9 covers housing and small buildings — commonly ≤3 storeys and ≤600 m² building area. Part 3 covers larger or more complex buildings (fire protection, occupant safety, accessibility), including most multi-unit and non-residential projects.',
    applicant: 'Applicant / owner',
    email: 'Contact email',
    storeys: 'Number of storeys',
    storeysPlaceholder: 'e.g. 2',
    storeysHint: 'Part 9 screening commonly uses ≤3 storeys.',
    gfa: 'Gross floor area',
    gfaUnit: 'm²',
    gfaPlaceholder: 'e.g. 240',
    gfaHint: 'Enter area in square metres (m²). Part 9 screening commonly uses ≤600 m².',
    proposedUse: 'Proposed use / occupancy',
    notes: 'Additional notes',
    files: 'Permit package files',
    dropzone: 'Drop PDF, CAD (DWG/DXF), or BIM/IFC files here, or click to browse',
    dropzoneHint: 'Up to {max} files · 15MB each · PDF, IFC, DWG, DXF, RVT, TXT, CSV',
    remove: 'Remove',
    submit: 'Submit permit package (free)',
    submitting: 'Reviewing package…',
    preparing: 'Preparing permit package…',
    running: 'Running deterministic checks, then AI-assisted findings…',
    complete: 'Review complete — {count} findings (deterministic + AI). Free grant prototype.',
    addressRequired: 'Project address is required.',
    resultsTitle: 'Preliminary compliance findings',
    statusModel:
      '4-state model: Meets / Does not meet / Information not available / Uncertain',
    requestId: 'Request ID',
    classificationNotes: 'Classification notes',
    typologyCardTitle: 'NBC typology classification',
    typologyCardEmpty: 'No typology selected — classification remains information not available.',
    typologyCardPart9House:
      'Part 9 house / single dwelling selected. Screening uses Part 9-oriented package checks (≤3 storeys / ≤600 m² heuristics).',
    typologyCardPart9Townhouse:
      'Part 9 duplex / townhouse / row house selected. Confirm dwelling-unit count and local multiplex / zoning rules.',
    typologyCardPart9Suite:
      'Part 9 secondary suite / ADU pathway selected. Confirm suite provisions and local zoning before formal submission.',
    typologyCardPart9Small:
      'Part 9 small-building pathway selected. Confirm the project still falls within Part 9 size/storey limits.',
    typologyCardPart3Residential:
      'Part 3 multi-unit residential selected. Major occupancy and Subsection 3.2.2. confirmation required (human-in-the-loop).',
    typologyCardPart3Assembly:
      'Part 3 assembly / business / mercantile selected. Occupancy group and 3.2.2. construction requirements need human confirmation.',
    typologyCardPart3Care:
      'Part 3 care / treatment occupancy selected. Higher life-safety scrutiny — escalate uncertain items for official review.',
    typologyCardPart3Industrial:
      'Part 3 industrial / high-hazard selected. Confirm hazard classification and applicable Part 3 provisions against drawings/BIM.',
    typologyCardPart3:
      'Part 3 (other / mixed) selected. Major occupancy / Subsection 3.2.2. confirmation is required (human-in-the-loop).',
    typologyCardOther: 'Typology uncertain — human classification of Part 3 vs Part 9 is required.',
    bilingualNote:
      'Bilingual UI is available now. Full French National Building Code / Quebec Construction Code machine-executable rule packs are a Phase 2 deliverable (NRC can supply French sample codes post-award).',
    hitlTitle: 'Human-in-the-loop review',
    hitlHelp:
      'Building-official workflow: Accept the system result, Override with a new 4-state determination and note, or Escalate uncertain/health-safety items for case-by-case assessment. Actions are recorded in the audit trail below.',
    colStatus: 'Status',
    colTopic: 'Topic',
    colRationale: 'Rationale',
    colCode: 'Code reference',
    colSource: 'Source',
    colReviewer: 'Reviewer',
    sourceDeterministic: 'Deterministic',
    sourceAi: 'AI-assisted',
    accept: 'Accept',
    override: 'Override',
    escalate: 'Escalate',
    change: 'Change',
    saveOverride: 'Save override',
    cancel: 'Cancel',
    newStatus: 'New status',
    overrideNote: 'Override note (required)',
    overridePlaceholder: 'Cite why the system result is being changed…',
    overrideNoteRequired: 'Add a short override note for {id} (required for auditability).',
    accepted: 'Accepted',
    overridden: 'Overridden',
    escalated: 'Escalated for human review',
    acceptNote: 'Accepted system determination',
    escalateNote: 'Escalated for case-by-case human assessment',
    was: 'Was',
    humanReviewed: 'Human reviewed',
    auditTitle: 'Audit trail',
    auditEmpty:
      'No reviewer actions yet. Accept, override, or escalate a finding to create an auditable record with timestamp and code reference.',
    auditTime: 'Time',
    auditFinding: 'Finding',
    auditAction: 'Action',
    auditChange: 'Status change',
    auditNote: 'Note / code ref',
    missingInfo: 'Missing information',
    humanSteps: 'Recommended human review steps',
    exportCsv: 'Export CSV',
    exportPdf: 'Print / PDF',
    meets: 'Meets',
    does_not_meet: 'Does not meet',
    information_not_available: 'Information not available',
    uncertain: 'Uncertain',
    countMeets: 'Meets',
    countFail: 'Does not meet',
    countIna: 'Info N/A',
    countUncertain: 'Uncertain',
    rules: 'rules',
    execution: 'execution',
    flowTitle: 'What happens in a review',
    flow1: 'Deterministic rules',
    flow1Desc: 'Machine-executable JSON checks run first',
    flow2: 'AI assist',
    flow2Desc: 'Fills gaps only where facts are incomplete',
    flow3: '4-state findings',
    flow3Desc: 'Meets / Does not meet / Info N/A / Uncertain',
    flow4: 'Human-in-the-loop',
    flow4Desc: 'Accept, override, or escalate with audit trail',
    flow5: 'Export',
    flow5Desc: 'CSV download or print/PDF for AHJ workflows',
    sampleLoad: 'Load sample Part 9 package',
    sampleLoaded: 'Sample Part 9 package loaded. Review the form, then submit (free).',
    sampleRun: 'Load sample & run review',
    apiTitle: 'Open API',
    apiHelp:
      'Programmatic access for custom UIs or AHJ e-permitting integrations. Free grant prototype — no auth required.',
    apiAnalyze: 'POST /api/permit-review/analyze',
    apiRuleset: 'GET /api/permit-review/ruleset',
    apiCopy: 'Copy curl',
    apiCopied: 'Copied',
  },
};

/** Widen string literals so FR (and other locales) can diverge from English copy. */
type DeepStringify<T> = {
  [K in keyof T]: T[K] extends string ? string : DeepStringify<T[K]>;
};

export type TranslationDict = DeepStringify<typeof en>;
export default en;
